// Standalone Support Page — accesible en /soporte sin entrar al dashboard.
// Soporta login de usuarios (okuser+PIN) y negocios (okuser+PIN).
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Headphones, ArrowLeft, LogOut, User as UserIcon,
  Building2, Loader2, LifeBuoy, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SupportModule from './SupportModule';
import { setSession, getSession, clearSession } from '../utils/embedAuth';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ── PIN Keypad ────────────────────────────────────────────────────────────── */
const PinKeypadMini = ({ onComplete, title, subtitle }) => {
  const [pin, setPin] = useState('');
  const handleNum = (n) => {
    if (pin.length >= 4) return;
    const np = pin + n;
    setPin(np);
    if (np.length === 4) setTimeout(() => { onComplete(np); setPin(''); }, 150);
  };
  return (
    <div className="text-center">
      <h3 className="text-white font-semibold text-lg">{title}</h3>
      {subtitle && <p className="text-gray-400 text-sm mt-1 mb-5">{subtitle}</p>}
      <div className="flex justify-center gap-2 mb-6">
        {[0,1,2,3].map(i => (
          <div key={i} className={`w-3 h-3 rounded-full transition-all ${i < pin.length ? 'bg-red-500 scale-110' : 'bg-white/15'}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 max-w-[260px] mx-auto">
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} onClick={() => handleNum(String(n))}
            className="h-14 bg-white/5 hover:bg-white/10 text-white text-xl rounded-xl font-medium transition-colors">{n}</button>
        ))}
        <div />
        <button onClick={() => handleNum('0')}
          className="h-14 bg-white/5 hover:bg-white/10 text-white text-xl rounded-xl font-medium">0</button>
        <button onClick={() => setPin(p => p.slice(0,-1))}
          className="h-14 text-gray-400 hover:bg-white/5 rounded-xl text-sm">⌫</button>
      </div>
    </div>
  );
};

/* ── Shared Header ─────────────────────────────────────────────────────────── */
const SupportHeader = ({ user, onLogout }) => {
  const navigate = useNavigate();
  return (
    <header className="border-b border-white/5 bg-black/40 backdrop-blur-sm sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
          <ArrowLeft size={16} /> OkBot
        </button>
        <div className="flex items-center gap-2">
          <Headphones size={18} className="text-red-500" />
          <span className="text-white font-semibold hidden sm:inline">Centro de Soporte</span>
        </div>
        {user ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              {user.role === 'business'
                ? <Building2 size={14} className="text-purple-400" />
                : <UserIcon size={14} className="text-blue-400" />
              }
              <span className="text-xs text-gray-400 hidden md:inline">
                {user.okuser ? `@${user.okuser}` : user.name}
              </span>
            </div>
            <button onClick={onLogout} title="Cerrar sesión"
              className="p-1.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="w-24" />
        )}
      </div>
    </header>
  );
};

/* ── Business Support View ─────────────────────────────────────────────────── */
const BusinessSupportView = ({ user }) => {
  // Para negocios reutilizamos el mismo SupportModule pero con role=business.
  // El módulo usa user.id para las APIs de tickets, que funciona igual para negocios.
  return (
    <div className="space-y-4">
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
        <Building2 className="text-purple-400 shrink-0" size={18} />
        <div>
          <p className="text-sm font-medium text-white">{user.name}</p>
          <p className="text-xs text-gray-400">Soporte empresarial · {user.okuser ? `@${user.okuser}` : user.email || ''}</p>
        </div>
      </div>
      <SupportModule user={user} />
    </div>
  );
};

/* ── Main Component ────────────────────────────────────────────────────────── */
const SoportePage = () => {
  const [user, setUser]           = useState(null);
  const [step, setStep]           = useState('checking');   // checking | type | id | pin | ready
  const [loginType, setLoginType] = useState('user');       // 'user' | 'business'
  const [identifier, setIdentifier] = useState('');
  const [tempData, setTempData]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const navigate = useNavigate();

  // Auto-login si hay sesión compartida desde el dashboard principal
  useEffect(() => {
    const sess = getSession();
    if (sess?.user?.id) {
      setUser(sess.user);
      setStep('ready');
    } else {
      setStep('type');
    }
  }, []);

  /* ── Paso 1: elegir tipo ── */
  const handleSelectType = (type) => {
    setLoginType(type);
    setStep('id');
    setError('');
    setIdentifier('');
  };

  /* ── Paso 2: verificar identifier ── */
  const handleCheckId = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setLoading(true); setError('');
    try {
      const clean = identifier.trim().replace(/^@/, '').toLowerCase();
      const endpoint = loginType === 'business'
        ? `${API}/auth/check-business/${clean}`
        : `${API}/auth/check-user/${clean}`;
      const res = await axios.get(endpoint);
      if (res.data?.success && res.data?.data) {
        setTempData(res.data.data);
        setStep('pin');
      } else {
        setError(loginType === 'business' ? 'Negocio no encontrado' : 'Usuario no encontrado');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'No encontrado');
    } finally { setLoading(false); }
  };

  /* ── Paso 3: PIN ── */
  const handlePin = async (pin) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/auth/login`, {
        user_id: tempData.id, pin
      });
      if (res.data?.success) {
        const u = res.data?.data?.user || { id: tempData.id, name: tempData.name, role: loginType === 'business' ? 'business' : 'user', okuser: tempData.okuser };
        setUser(u);
        setSession({ user: u, token: res.data?.data?.token || '' });
        setStep('ready');
        toast.success(`Bienvenido${u.name ? ', ' + u.name : ''}`);
      } else {
        setError('PIN incorrecto');
        setStep('pin');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'PIN incorrecto');
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
    setStep('type');
    setTempData(null);
    setIdentifier('');
    setError('');
  };

  /* ── Pantalla de carga ── */
  if (step === 'checking') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  /* ── Vista logueada ── */
  if (step === 'ready' && user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
        <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px' } }} />
        <SupportHeader user={user} onLogout={handleLogout} />
        <main className="flex-1 max-w-6xl w-full mx-auto px-3 py-4">
          {user.role === 'business'
            ? <BusinessSupportView user={user} />
            : <SupportModule user={user} />
          }
        </main>
        <footer className="text-center py-3 text-[11px] text-gray-600 border-t border-white/5">
          OkGo Soporte · <a href="https://okbot.okgo.mx" className="hover:text-gray-400">okbot.okgo.mx</a>
        </footer>
      </div>
    );
  }

  /* ── Vista de auth ── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a] flex flex-col">
      <Toaster position="top-center" toastOptions={{ style: { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px' } }} />
      <SupportHeader user={null} onLogout={null} />

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">

          {/* Branding */}
          <div className="text-center mb-7">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-red-900/30">
              <LifeBuoy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Centro de Soporte</h1>
            <p className="text-gray-400 text-sm mt-1">Estamos aquí para ayudarte</p>
          </div>

          <div className="bg-[#0d1117] border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl">

            {/* ── Paso: elegir tipo ────────────────────────────────── */}
            {step === 'type' && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 text-center uppercase tracking-wider mb-4">¿Cómo ingresarás?</p>
                <button onClick={() => handleSelectType('user')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-blue-500/40 hover:bg-blue-500/5 transition-all group text-left">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-blue-500/30 transition-colors">
                    <UserIcon className="text-blue-400" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Soy Usuario</p>
                    <p className="text-xs text-gray-500 mt-0.5">Tickets, chat y soporte personal</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-600 group-hover:text-gray-300 transition-colors" />
                </button>

                <button onClick={() => handleSelectType('business')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-purple-500/40 hover:bg-purple-500/5 transition-all group text-left">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-purple-500/30 transition-colors">
                    <Building2 className="text-purple-400" size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Soy Negocio</p>
                    <p className="text-xs text-gray-500 mt-0.5">Soporte empresarial y gestión</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-600 group-hover:text-gray-300 transition-colors" />
                </button>
              </div>
            )}

            {/* ── Paso: identificador ──────────────────────────────── */}
            {step === 'id' && (
              <form onSubmit={handleCheckId} className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <button type="button" onClick={() => { setStep('type'); setError(''); }}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                    <ArrowLeft size={15} />
                  </button>
                  <div className="flex items-center gap-2">
                    {loginType === 'business'
                      ? <Building2 size={15} className="text-purple-400" />
                      : <UserIcon size={15} className="text-blue-400" />
                    }
                    <span className="text-sm text-gray-300 font-medium">
                      {loginType === 'business' ? 'Acceso para Negocios' : 'Acceso para Usuarios'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1.5 block">
                    {loginType === 'business' ? 'OkUser del negocio' : 'Tu OkUser'}
                  </label>
                  <div className="flex">
                    <span className="px-3 flex items-center bg-white/5 border border-r-0 border-white/10 rounded-l-xl text-gray-500 text-sm">@</span>
                    <input type="text" value={identifier} onChange={e => setIdentifier(e.target.value)}
                      placeholder={loginType === 'business' ? 'mi_negocio' : 'tu_usuario'} autoFocus
                      className="flex-1 bg-white/5 border border-white/10 rounded-r-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-red-500/50 transition-colors" />
                  </div>
                </div>
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <button type="submit" disabled={loading || !identifier.trim()}
                  className="w-full py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight size={15} />} Continuar
                </button>

                {loginType === 'user' && (
                  <p className="text-center text-xs text-gray-500 pt-1">
                    ¿No tienes cuenta?{' '}
                    <button type="button" onClick={() => navigate('/')} className="text-red-400 hover:underline">Regístrate</button>
                  </p>
                )}
              </form>
            )}

            {/* ── Paso: PIN ────────────────────────────────────────── */}
            {step === 'pin' && (
              <>
                <div className="flex items-center gap-2 mb-5">
                  <button type="button" onClick={() => { setStep('id'); setError(''); }}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors">
                    <ArrowLeft size={15} />
                  </button>
                  <span className="text-sm text-gray-400">Cambiar usuario</span>
                </div>
                <PinKeypadMini
                  onComplete={handlePin}
                  title="Ingresa tu PIN"
                  subtitle={`Hola, ${tempData?.name || identifier}`}
                />
                {error && <p className="text-red-400 text-xs text-center mt-3">{error}</p>}
                {loading && (
                  <div className="flex justify-center mt-4">
                    <Loader2 className="w-5 h-5 text-red-500 animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>

          <p className="text-center text-[11px] text-gray-600 mt-5">
            OkGo Soporte ·{' '}
            <button onClick={() => navigate('/terminos')} className="hover:text-gray-400">Términos</button>{' '}·{' '}
            <button onClick={() => navigate('/privacidad')} className="hover:text-gray-400">Privacidad</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SoportePage;
